/**
 * Formatters — Indian locale conventions throughout (en-IN).
 *
 * IMPORTANT: All money values from the backend are in **paise** (integer).
 * Never call .toFixed() / arithmetic on a "rupee" value — convert at the
 * very last moment, in the formatter, to avoid floating-point drift.
 */

const inrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  maximumFractionDigits: 0,
});

const inrFormatterWithPaise = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const compactInrFormatter = new Intl.NumberFormat('en-IN', {
  style: 'currency',
  currency: 'INR',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const dateFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
});

const dateTimeFormatter = new Intl.DateTimeFormat('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: true,
});

const relativeFormatter = new Intl.RelativeTimeFormat('en-IN', {
  numeric: 'auto',
});

/** paise → "₹1,234" (no decimals when round) */
export function formatINR(paise: number): string {
  return inrFormatter.format(paise / 100);
}

/** paise → "₹1,234.50" (always 2 decimals — for invoices, GST breakdowns) */
export function formatINRPrecise(paise: number): string {
  return inrFormatterWithPaise.format(paise / 100);
}

/** paise → "₹1.2L" or "₹50K" — for stat cards */
export function formatINRCompact(paise: number): string {
  return compactInrFormatter.format(paise / 100);
}

/** Render a paise + GST rate combo as a "₹X (incl. Y% GST)" string */
export function formatINRWithGstNote(paise: number, gstRate: number): string {
  return `${formatINR(paise)} (incl. ${(gstRate * 100).toFixed(0)}% GST)`;
}

/** ISO string | Date | epoch → "10 May 2026" */
export function formatDate(input: string | Date | number): string {
  const date = input instanceof Date ? input : new Date(input);
  return dateFormatter.format(date);
}

/** ISO string | Date | epoch → "10 May 2026, 03:45 PM" */
export function formatDateTime(input: string | Date | number): string {
  const date = input instanceof Date ? input : new Date(input);
  return dateTimeFormatter.format(date);
}

/** Render relative time: "2 days ago", "in 3 hours" */
export function formatRelative(input: string | Date | number): string {
  const date = input instanceof Date ? input : new Date(input);
  const diffMs = date.getTime() - Date.now();
  const diffSec = Math.round(diffMs / 1000);
  const abs = Math.abs(diffSec);

  if (abs < 60) return relativeFormatter.format(diffSec, 'second');
  if (abs < 3600) return relativeFormatter.format(Math.round(diffSec / 60), 'minute');
  if (abs < 86_400) return relativeFormatter.format(Math.round(diffSec / 3600), 'hour');
  if (abs < 30 * 86_400) return relativeFormatter.format(Math.round(diffSec / 86_400), 'day');
  if (abs < 365 * 86_400) return relativeFormatter.format(Math.round(diffSec / (30 * 86_400)), 'month');
  return relativeFormatter.format(Math.round(diffSec / (365 * 86_400)), 'year');
}

/** Sluggify a string — lowercase, hyphens, no punctuation. Used in URLs. */
export function slugify(input: string): string {
  return input
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // strip diacritics
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

/** Truncate a string with an ellipsis */
export function truncate(input: string, max: number): string {
  if (input.length <= max) return input;
  return `${input.slice(0, max - 1).trimEnd()}…`;
}

/** Pluralise English nouns (tiny — handles the common cases). */
export function pluralize(count: number, singular: string, plural?: string): string {
  if (count === 1) return `${count} ${singular}`;
  return `${count} ${plural ?? `${singular}s`}`;
}

/** Format a phone number in Indian style: "+91 98765 43210" */
export function formatPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits.slice(0, 5)} ${digits.slice(5)}`;
  if (digits.length === 12 && digits.startsWith('91')) {
    return `+91 ${digits.slice(2, 7)} ${digits.slice(7)}`;
  }
  return input; // unknown shape — return as-is
}

/** Indian pincode validation — 6 digits, first ≠ 0 */
export function isValidPincode(input: string): boolean {
  return /^[1-9][0-9]{5}$/.test(input);
}
